import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Table from '../Components/Tables';
import Button from '../Components/Buttons';
import { AgentFrameApiProvider, useAgentFrameApi } from '../Services/AgentFrameApiProvider';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { AlertProvider, useAlert } from '../Components/AlertContext';
import { useConfirmation } from '../Components/ConfirmationContext';
import { formatDateUTC } from '../utils/formatDateUTC';

export function AgentFramesContent({ canEdit }) {

    const { organizationId } = useParams();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const { showConfirmation } = useConfirmation();
    const {
        getAgentFrames,
        getAgentFrameById,
        deleteAgentFrame,
        createAgentFrame
    } = useAgentFrameApi();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [alertShown, setAlertShown] = useState(false);
    const [items, setItems] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [selectedRows, setSelectedRows] = useState([]);
    const selectedId = selectedRows.length === 1 ? selectedRows[0].id : null;

    const columns = [ 
        { name: 'ID', selector: (row) => row.id, sortable: false },
        { name: 'Name', selector: (row) => row.name, sortable: false },
        { name: 'Type', selector: (row) => row.frame_type === "CRITERIA" ? "Criteria Based" : row.frame_type === "THEMATIC" ? "Thematics Based" : "Topic Based", sortable: false },
        { name: 'Status', selector: (row) => row.is_active === true ? "Active" : "InActive", sortable: false },
        { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: false },
        { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: false },
    ];

    const handleDelete = (row) => {
        showConfirmation(
            `Are you sure you want to delete the selected item?`,
            async () => {
                try {
                    setLoading(true);
                    await deleteAgentFrame(row);
                    await fetchItems(0, itemsPerPage);
                    showAlert('success', 'Item was successfully deleted!');
                } catch (error) {
                    showAlert('fail', 'Failed to delete the item!');
                } finally {
                    setLoading(false);
                }
            }
        );
    }

    const handleRowClicked = (row) => {
        navigate(`/organizations/${organizationId}/agent-frames/${row.id}`, { state: { organizationId } });
    }

    const createCopy = async () => {
        if (!selectedId) return;
        setLoading(true);

        try {
            const frameToCopy = await getAgentFrameById(selectedId);

            if (!frameToCopy) {
                showAlert('fail', 'Frame not found');
                setLoading(false);
                return;
            }

            const escapeRegExp = (string) =>
                string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

            const baseName = frameToCopy.name;
            const safeBaseName = escapeRegExp(baseName);
            const copyNumbers = items
                .map(a => {
                    const match = a.name.match(new RegExp(`^${safeBaseName} \\(Copy (\\d+)\\)$`));
                    return match ? parseInt(match[1], 10) : null;
                })
                .filter(n => n !== null);
            const nextCopyNumber = copyNumbers.length > 0 ? Math.max(...copyNumbers) + 1 : 1;

            const newFrameData = {
                name: `${baseName} (Copy ${nextCopyNumber})`,
                frame_type: frameToCopy.frame_type,
                topic: frameToCopy.topic ?? null,
                criteria: frameToCopy.criteria ?? null,
                thematics: frameToCopy.thematics ?? [],
                instruction: frameToCopy.instruction ?? null,
                example: frameToCopy.example ?? null,
                is_active: frameToCopy.is_active,
            };

            await createAgentFrame(newFrameData);

            await fetchItems(0, itemsPerPage);

            showAlert('success', 'Frame copy was successfully created!');
        } catch (error) {
            console.error("Create copy error:", error);
            showAlert('fail', 'Failed to create a copy of the frame!');
        } finally {
            setLoading(false);
        }
    };

    const fetchItems = async (skip, limit) => {
        if (limit !== itemsPerPage) {
            setItemsPerPage(limit);
        }

        setLoading(true);
        setError(null);
        try {
            const response = await getAgentFrames(skip, limit);
            setItems(response.items);
            setTotalItems(response.totalCount);
        } catch (error) {
            setError(error.message);
            showAlert('fail', "Failed to fetch agents frames");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchItems(0, itemsPerPage);
    }, []);
    console.log("Items", items);

    return (
        <div className="container">
            <Breadcrumbs />
            <div className="row d-flex gap">
                <div className="radius-yes p-2 bg-yes col-12">
                    {error ? (
                        <div>Error: {error}</div>
                    ) : (
                        <>
                            <Table
                                title="Agent Frames"
                                columns={columns}
                                data={items}
                                selectableRows
                                selectableRowsHighlight
                                paginationPerPage={itemsPerPage}
                                progressPending={loading}
                                paginationTotalRows={totalItems}
                                onRowClicked={handleRowClicked}
                                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                                onDelete={handleDelete}
                                onChangePage={fetchItems}
                                getRowUrl={(row) => `/organizations/${organizationId}/agent-frames/${row.id}`}
                                searchFields={['name']}
                                typeSearch = 'default'
                                onSelectedRowsChange={({ selectedRows }) => {
                                    setSelectedRows(selectedRows);
                                }}
                            />
                            {canEdit &&(
                                <Button
                                    background="color2"
                                    size="large"
                                    onClick={() => navigate(`/organizations/${organizationId}/agent-frames/new-agent-frame`)}
                                >
                                    Create New Agent Frame
                                </Button>
                            )}

                            {selectedId && (
                                <Button variant="ghost" size="medium" onClick={createCopy}>
                                    Create copy
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export function AgentFrames({ canEdit }) {
    return (
        <AlertProvider>
            <ConfirmationProvider>
                <AgentFrameApiProvider>
                    <AgentFramesContent canEdit={canEdit} />
                </AgentFrameApiProvider>
            </ConfirmationProvider>
        </AlertProvider>
    );
}

export default AgentFrames;